import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Button,
  makeStyles,
  Paper,
  IconButton,
  Tooltip,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import SearchIcon from '@material-ui/icons/Search';
import ClearIcon from '@material-ui/icons/Clear';
import RestoreIcon from '@material-ui/icons/Restore';
import { SortOption } from '../../types';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    width: '400px',
    overflow: 'auto',
    height: '100%',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(3),
  },
  formControl: {
    width: '100%',
  },
  section: {
    marginBottom: theme.spacing(2),
  },
  buttonsContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.spacing(4),
    '& > *': {
      marginLeft: theme.spacing(1),
    },
  },
  clearButton: {
    position: 'absolute',
    right: 8,
    top: '50%',
    transform: 'translateY(-50%)',
    padding: 4,
    zIndex: 1,
  },
  inputWrapper: {
    position: 'relative',
  },
  headerDescription: {
    marginTop: theme.spacing(0.5),
    display: 'flex',
    alignItems: 'center',
  },
  closeButton: {
    backgroundColor: theme.palette.background.default,
    height: 30,
    width: 30,
    '&:hover': {
      backgroundColor: theme.palette.grey[200],
    },
  },
}));

interface IssueFilterAndSearchProps {
  onClose: () => void;
  currentFilters: Record<string, string>;
  onApplyFilters: (filters: Record<string, string>) => void;
  onSearch: (query: string) => void;
}

export const IssueFilterAndSearch: React.FC<IssueFilterAndSearchProps> = ({
  onClose,
  currentFilters,
  onApplyFilters,
  onSearch,
}) => {
  const classes = useStyles();
  const [filters, setFilters] = useState<Record<string, string>>(currentFilters);
  const [searchQuery, setSearchQuery] = useState(currentFilters.query || '');
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    setFilters(currentFilters);
    setSearchQuery(currentFilters.query || '');
    
    // Check if current query contains status filters
    const query = currentFilters.query || '';
    if (query.includes('is:resolved')) {
      setStatusFilter('resolved');
    } else if (query.includes('is:unresolved')) {
      setStatusFilter('unresolved');
    } else if (query.includes('is:ignored')) {
      setStatusFilter('ignored');
    } else {
      setStatusFilter('');
    }
  }, [currentFilters]);

  const handleChange = (field: string, value: string) => {
    setFilters(prevState => ({
      ...prevState,
      [field]: value,
    }));
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    if (value) {
      setStatusFilter(''); 
    }
  };

  const handleStatusChange = (value: string) => {
    setStatusFilter(value);
    if (value) {
      setSearchQuery(''); 
    }
  };

  const handleApply = () => {
    let finalQuery = '';
    
    if (statusFilter) {
      finalQuery = `is:${statusFilter}`;
    } else if (searchQuery) {
      finalQuery = searchQuery;
    }
    
    const updatedFilters = {
      ...filters,
      query: finalQuery,
    };
    
    onApplyFilters(updatedFilters);
    onSearch(finalQuery);
  };

  const handleReset = () => {
    const currentLimit = filters.limit || currentFilters.limit;
    
    const resetFilters = {
      sort: '-last_seen',
      limit: currentLimit,
      environment: '',
      query: '',
    };
    setFilters(resetFilters);
    setSearchQuery('');
    setStatusFilter('');
    onApplyFilters(resetFilters);
    onSearch('');
  };

  return (
    <Paper className={classes.root}>
      <Box className={classes.header}>
        <Box>
          <Typography variant="h6">
            Filter & Search
          </Typography>
          <Typography color="textSecondary" variant="body2" className={classes.headerDescription}>
            Use the options below to narrow down the errors shown in the table.
          </Typography>
        </Box>
        <Tooltip title="Close Filter">
          <IconButton
            onClick={onClose}
            className={classes.closeButton}
            size="small"
            aria-label="close filter"
          >
            <CloseIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <Box mt={3}>
        <div className={classes.section}>
          <Grid container spacing={2}>
            {/* Search */}
            <Grid item xs={12}>
              <div className={classes.inputWrapper}>
                <TextField
                  label="Search Issues"
                  variant="outlined"
                  fullWidth
                  value={searchQuery}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  disabled={!!statusFilter}
                  size="small"
                  placeholder="Search by title, message, or stack trace..."
                />
                {searchQuery && (
                  <IconButton
                    className={classes.clearButton}
                    onClick={() => handleSearchChange('')}
                    size="small"
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                )}
              </div>
            </Grid>

            {/* Status Filter */}
            <Grid item xs={12}>
              <FormControl variant="outlined" className={classes.formControl} size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => handleStatusChange(e.target.value as string)}
                  disabled={!!searchQuery}
                  label="Status"
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="resolved">Resolved</MenuItem>
                  <MenuItem value="unresolved">Unresolved</MenuItem>
                  <MenuItem value="ignored">Ignored</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Environment Filter */}
            <Grid item xs={12}>
              <FormControl variant="outlined" className={classes.formControl} size="small">
                <InputLabel>Environment</InputLabel>
                <Select
                  value={filters.environment || ''}
                  onChange={(e) => handleChange('environment', e.target.value as string)}
                  label="Environment"
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="production">Production</MenuItem>
                  <MenuItem value="staging">Staging</MenuItem>
                  <MenuItem value="development">Development</MenuItem>
                  <MenuItem value="test">Test</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Sort By */}
            <Grid item xs={12}>
              <FormControl variant="outlined" className={classes.formControl} size="small">
                <InputLabel>Sort By</InputLabel>
                <Select
                  value={filters.sort || '-last_seen'}
                  onChange={(e) => handleChange('sort', e.target.value as string)}
                  label="Sort By"
                >
                  <MenuItem value={SortOption.LAST_SEEN}>Last Seen</MenuItem>
                  <MenuItem value={SortOption.MOST_FREQUENT}>Most Frequent</MenuItem>
                  <MenuItem value={SortOption.LEAST_FREQUENT}>Least Frequent</MenuItem>
                  <MenuItem value={SortOption.HIGHEST_PRIORITY}>Highest Priority</MenuItem>
                  <MenuItem value={SortOption.LOWEST_PRIORITY}>Lowest Priority</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </div>

        <div className={classes.buttonsContainer}>
          <Button
            onClick={handleReset}
            color="secondary"
            variant="outlined"
            startIcon={<RestoreIcon />}
          >
            Reset
          </Button>
          <Button
            onClick={handleApply}
            variant="contained"
            color="primary"
            startIcon={<SearchIcon />}
          >
            Apply Filters
          </Button>
        </div>
      </Box>
    </Paper>
  );
};